import axios, { AxiosError } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const API_KEY =
  import.meta.env.VITE_API_KEY ||
  (typeof window !== 'undefined' ? window.localStorage.getItem('apiKey') || '' : '');

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    ...(API_KEY && { 'X-API-Key': API_KEY }),
  },
});

export function setApiKey(apiKey: string) {
  const trimmed = apiKey.trim();
  if (!trimmed) return;
  apiClient.defaults.headers.common['X-API-Key'] = trimmed;
  if (typeof window !== 'undefined') {
    window.localStorage.setItem('apiKey', trimmed);
  }
}

export function clearApiKey() {
  delete apiClient.defaults.headers.common['X-API-Key'];
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('apiKey');
  }
}

function isFormData(value: unknown): value is FormData {
  return typeof FormData !== 'undefined' && value instanceof FormData;
}

function isBlob(value: unknown): value is Blob {
  return typeof Blob !== 'undefined' && value instanceof Blob;
}

function safeTryParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

// Add request interceptor for debugging
apiClient.interceptors.request.use(
  (config) => {
    // IMPORTANT:
    // - Don't force Content-Type globally: it breaks multipart/form-data uploads (missing boundary).
    // - For FormData, let Axios/browser set Content-Type with the correct boundary.
    if (isFormData(config.data)) {
      if (config.headers && typeof config.headers === 'object') {
        const maybeAxiosHeaders = config.headers as unknown as { delete?: (name: string) => void };
        if (typeof maybeAxiosHeaders.delete === 'function') {
          maybeAxiosHeaders.delete('Content-Type');
          maybeAxiosHeaders.delete('content-type');
        } else {
          const headersRecord = config.headers as unknown as Record<string, unknown>;
          delete headersRecord['Content-Type'];
          delete headersRecord['content-type'];
        }
      }
    }

    // Get token from localStorage if available
    const token = typeof window !== 'undefined' ? window.localStorage.getItem('authToken') : null;
    
    // If Authorization header (JWT token) is present, don't send X-API-Key
    // JWT authentication takes precedence over API key
    if (token) {
      // Set Authorization header if token exists
      config.headers['Authorization'] = `Bearer ${token}`;
      // Remove API key when using JWT
      delete config.headers['X-API-Key'];
      delete config.headers['x-api-key'];
    } else {
      // Otherwise, use API key if available (for backwards compatibility)
      // Only send API key for non-auth routes (auth routes don't need it)
      const isAuthRoute = config.url?.startsWith('/auth/');
      if (!isAuthRoute && API_KEY) {
        config.headers['X-API-Key'] = API_KEY;
      }
    }

    console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config);
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // For blob responses, log size instead of content
    if (response.config.responseType === 'blob') {
      const blob = response.data as Blob;
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        responseType: 'blob',
        size: blob?.size || 0,
        mimeType: blob?.type || 'unknown',
      });
    } else {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
    }
    return response;
  },
  async (error: AxiosError) => {
    const errorMessage = error.message || '';
    
    // Suppress runtime.lastError warnings from browser extensions
    if (
      errorMessage.includes('runtime.lastError') ||
      errorMessage.includes('message port closed') ||
      errorMessage.includes('message channel closed') ||
      errorMessage.includes('asynchronous response')
    ) {
      // Silently ignore browser extension errors - they're not from our code
      return Promise.reject(error);
    }
    
    // If responseType was "blob", Axios will give us a Blob even for JSON errors.
    // Decode it so logs show the real server error instead of "{}".
    let decodedServerError: unknown = undefined;
    if (isBlob(error.response?.data)) {
      try {
        const blob = error.response?.data as Blob;
        const text = await blob.text();
        decodedServerError = safeTryParseJson(text) ?? text.slice(0, 4000);
      } catch {
        // ignore decoding errors
      }
    }

    const errorDetails = {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      decodedServerError,
      // Include full error response for debugging
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        headers: error.response.headers,
        data: error.response.data,
      } : undefined,
    };
    
    console.error('[API Response Error]', errorDetails);
    
    // If we have detailed error data from server, log it separately for visibility
    if (error.response?.data) {
      const toLog = decodedServerError ?? error.response.data;
      if (typeof toLog === 'string') {
        console.error('[API Error Details from Server]', toLog);
      } else {
        console.error('[API Error Details from Server]', JSON.stringify(toLog, null, 2));
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;


